import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Tabs,
  Tab,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  TextField,
  InputAdornment,
  Stack,
  Popover,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Radio,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";

import { AddCompanyModal } from "./AddCompanyModal";
import { AddUserModal } from "./AddUserModal";

import { useGetEverifyCompanies } from "../hooks/superAdminEverify/useGetEverifyCompanies";
import { useEverifyDeleteCompany } from "../hooks/superAdminEverify/useEverifyDeleteCompany";
import { useEverifyUpdateCompanyStatus } from "../hooks/superAdminEverify/useEverifyUpdateCompanyStatus";

import { useGetAllEverifyUsers } from "../hooks/superAdminEverify/useGetAllEverifyUsers";
import { useDeleteEverifyUser } from "../hooks/superAdminEverify/useDeleteEverifyUser";
import { useUpdateEverifyUserStatus } from "../hooks/superAdminEverify/useUpdateEverifyUserStatus";

import { toast } from "react-toastify";

const SORT_OPTIONS = [
  { value: "recency", label: "Recency (default)" },
  { value: "alphabetical", label: "Alphabetical (A-Z)" },
];

const DEFAULT_COMPANY_FILTER = {
  parent: false,
  child: false,
  active: false,
  inactive: false,
  sort: "recency",
};

const DEFAULT_USER_FILTER = {
  parent: false,
  child: false,
  active: false,
  inactive: false,
  sort: "recency",
};

export const UserCompanyTable = ({isSuperAdmin = true}) => {
  const [tab, setTab] = useState(0); // 0 = Companies, 1 = Users

  const [companySearch, setCompanySearch] = useState("");
  const [userSearch, setUserSearch] = useState("");

  const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [editCompany, setEditCompany] = useState(null);
  const [editUser, setEditUser] = useState(null);

  const [companyMenuAnchor, setCompanyMenuAnchor] = useState(null);
  const [companyMenuRow, setCompanyMenuRow] = useState(null);

  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [userMenuRow, setUserMenuRow] = useState(null);

  const [confirmDelete, setConfirmDelete] = useState(null); // { type: "company" | "user", row }
  const [isDeleting, setIsDeleting] = useState(false);

  let activeStatus = null;

  // Filter popover anchor
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);

  // Client-side filter state for companies
  const [companyFilter, setCompanyFilter] = useState({
    ...DEFAULT_COMPANY_FILTER,
  });

  // Client-side filter state for users
  const [userFilter, setUserFilter] = useState({
    ...DEFAULT_USER_FILTER,
  });
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Companies
  const {
    data: companies = [],
    refetch: refetchCompanies,
    isLoading: companiesLoading,
  } = useGetEverifyCompanies({ search: companySearch, isActive: activeStatus });

  const deleteCompany = useEverifyDeleteCompany();
  const updateCompanyStatus = useEverifyUpdateCompanyStatus();

  // Users
  const {
    data: users = [],
    refetch: refetchUsers,
    isLoading: usersLoading,
  } = useGetAllEverifyUsers(userSearch);

  const deleteUser = useDeleteEverifyUser();
  const updateUserStatus = useUpdateEverifyUserStatus();

  // Refetch when modals close
  useEffect(() => {
    if (!isCompanyModalOpen) refetchCompanies();
  }, [isCompanyModalOpen]);
  
  useEffect(() => {
    if (!isUserModalOpen) refetchUsers();
  }, [isUserModalOpen]);  

  // ---- Menus ----
  const handleCompanyMenuOpen = (event, row) => {
    setCompanyMenuAnchor(event.currentTarget);
    setCompanyMenuRow(row);
  };

  const handleCompanyMenuClose = () => {
    setCompanyMenuAnchor(null);
    setCompanyMenuRow(null);
  };

  const handleUserMenuOpen = (event, row) => {
    setUserMenuAnchor(event.currentTarget);
    setUserMenuRow(row);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
    setUserMenuRow(null);
  };

  // ---- Filter popover handlers ----
  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const pending = [refetchUsers()];
      if (isSuperAdmin) {
        pending.push(refetchCompanies());
      }
      await Promise.all(pending);
    } catch {
      toast.error("Failed to refresh data");
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleClearFilters = () => {
    if (tab === 0) {
      setCompanyFilter({
        ...DEFAULT_COMPANY_FILTER,
      });
    } else {
      setUserFilter({
        ...DEFAULT_USER_FILTER,
      });
    }
  };

  const filterOpen = Boolean(filterAnchorEl);

  // ---- Mutations ----
  const handleDeleteCompany = async (row) => {
    handleCompanyMenuClose();
    const id = toast.loading("Deleting company...");
    try {
      await deleteCompany.mutateAsync(row.id);
      toast.update(id, {
        render: "Company deleted",
        type: "success",
        isLoading: false,
        autoClose: 1500,
      });
      refetchCompanies();
    } catch (e) {
      toast.update(id, {
        render: "Failed to delete company",
        type: "error",
        isLoading: false,
        autoClose: 1500,
      });
    }
  };

  const handleToggleCompanyStatus = async (row) => {
    handleCompanyMenuClose();
    // Companies: Active = 0, Inactive = 1 (per your earlier note)
    const newStatus = row.status === 0 ? 1 : 0;
    const id = toast.loading("Updating status...");
    try {
      await updateCompanyStatus.mutateAsync({
        companyId: row.id,
        status: newStatus,
      });
      toast.update(id, {
        render: "Status updated",
        type: "success",
        isLoading: false,
        autoClose: 1500,
      });
      refetchCompanies();
    } catch (e) {
      toast.update(id, {
        render: "Failed to update status",
        type: "error",
        isLoading: false,
        autoClose: 1500,
      });
    }
  };

  const handleDeleteUser = async (row) => {
    handleUserMenuClose();
    const id = toast.loading("Deleting user...");
    try {
      await deleteUser.mutateAsync({
        userId: row.id,
        companyId: row.companyId,
      });
      toast.update(id, {
        render: "User deleted",
        type: "success",
        isLoading: false,
        autoClose: 1500,
      });
      refetchUsers();
    } catch (e) {
      toast.update(id, {
        render: "Failed to delete user",
        type: "error",
        isLoading: false,
        autoClose: 1500,
      });
    }
  };

  const confirmDeleteAction = async () => {
    if (!confirmDelete) return;
    setIsDeleting(true);
    try {
      if (confirmDelete.type === "company") {
        await handleDeleteCompany(confirmDelete.row);
      } else {
        await handleDeleteUser(confirmDelete.row);
      }
    } finally {
      setIsDeleting(false);
      setConfirmDelete(null);
    }
  };

  const handleToggleUserStatus = async (row) => {
    handleUserMenuClose();
    const newStatus = row.status === 1 ? 2 : 1;
    const id = toast.loading("Updating user status...");
    try {
      await updateUserStatus.mutateAsync({
        userId: row.id,
        status: newStatus,
        companyId: row.companyId,
      });
      toast.update(id, {
        render: "User status updated",
        type: "success",
        isLoading: false,
        autoClose: 1500,
      });
      refetchUsers();
    } catch (e) {
      toast.update(id, {
        render: "Failed to update user status",
        type: "error",
        isLoading: false,
        autoClose: 1500,
      });
    }
  };

  // ---- Render helpers ----
  const renderCompanyStatus = (status) => {
    const isActive = status === 0; // company: 0 = Active
    return (
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 500,
          color: isActive ? "#28A745" : "#A0A4AB",
        }}
      >
        {isActive ? "Active" : "Inactive"}
      </Typography>
    );
  };

  const handleTabChange = (_, v) => {
    setTab(isSuperAdmin ? v : 1); // force users tab when not superadmin
  };  

  const renderUserStatus = (status) => {
    let label = "Invited";
    let color = "#2563EB";

    if (status === 1) {
      label = "Active";
      color = "#28A745";
    } else if (status === 2) {
      label = "Inactive";
      color = "#A0A4AB";
    }

    return (
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 500,
          color,
        }}
      >
        {label}
      </Typography>
    );
  };

  const renderCompanyType = (row) => (row.isParent ? "Parent" : "Child");

  const renderUserRole = (row) => {
    if (row.roleId === 6) return "Company Admin";
    if (row.roleId === 7) return "HR User";
    return "-";
  };

  const filteredCompanies = useMemo(() => {
    const subset = companies.filter((company) => {
      const f = companyFilter;
      if (f.parent || f.child) {
        const isParent = company.isParent;
        if (isParent && !f.parent) return false;
        if (!isParent && !f.child) return false;
      }
      if (f.active || f.inactive) {
        if (company.status === 0 && !f.active) return false;
        if (company.status === 1 && !f.inactive) return false;
      }
      return true;
    });

    const sortPreference = companyFilter.sort || "recency";
    const sorted = [...subset];
    if (sortPreference === "alphabetical") {
      sorted.sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, {
          sensitivity: "base",
        })
      );
    } else {
      sorted.sort(
        (a, b) => Number(b.id ?? 0) - Number(a.id ?? 0)
      );
    }
    return sorted;
  }, [companies, companyFilter]);

  const filteredUsers = useMemo(() => {
    const subset = users.filter((user) => {
      const f = userFilter;
      if (f.parent || f.child) {
        const isParent = user.isParent;
        if (isParent && !f.parent) return false;
        if (!isParent && !f.child) return false;
      }
      if (f.active || f.inactive) {
        if (user.status === 1 && !f.active) return false;
        if (user.status === 2 && !f.inactive) return false;
        if (user.status !== 1 && user.status !== 2) return false;
      }
      return true;
    });

    const sortPreference = userFilter.sort || "recency";
    const sorted = [...subset];
    if (sortPreference === "alphabetical") {
      sorted.sort((a, b) => {
        const nameA = `${a.firstName || ""} ${a.lastName || ""}`.trim().toLowerCase();
        const nameB = `${b.firstName || ""} ${b.lastName || ""}`.trim().toLowerCase();
        return nameA.localeCompare(nameB);
      });
    } else {
      sorted.sort(
        (a, b) => Number(b.id ?? 0) - Number(a.id ?? 0)
      );
    }
    return sorted;
  }, [users, userFilter]);

  // Filter values in use for current tab
  const activeFilter = tab === 0 ? companyFilter : userFilter;
  const setActiveFilter = tab === 0 ? setCompanyFilter : setUserFilter;

  return (
    <Box sx={{ p: 3 }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: "16px",
          border: "1px solid #E2E2E2",
          backgroundColor: "#FFFFFF",
        }}
      >
        {/* Tabs Header */}
        <Box
          sx={{
            borderBottom: "1px solid #E5E7EB",
            px: 3,
            pt: 2,
          }}
        >
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            textColor="primary"
            indicatorColor="primary"
            sx={{
              minHeight: 48,
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: 14,
                minHeight: 48,
              },
            }}
          >
            {isSuperAdmin && <Tab label="Companies" />} 
            <Tab label="Users" />
          </Tabs>
        </Box>

        {/* Toolbar: Search, Filter, Add */}
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            display: "flex",
            alignItems: "center",
            gap: 2,
            justifyContent: "space-between",
            flexWrap: "wrap",
          }}
        >
          <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap", gap: 1 }}>
            <TextField
              size="small"
              placeholder={tab === 0 ? "Search company..." : "Search user..."}
              value={tab === 0 ? companySearch : userSearch}
              onChange={(e) =>
                tab === 0
                  ? setCompanySearch(e.target.value)
                  : setUserSearch(e.target.value)
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{
                minWidth: { xs: "100%", sm: 260 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "999px",
                  backgroundColor: "#F9FAFB",
                },
              }}
            />

            {/* Filter pill button */}
            <button
              type="button"
              onClick={handleFilterClick}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 16px",
                borderRadius: 999,
                border: "1px solid #E5E7EB",
                backgroundColor: "#FFFFFF",
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              <FilterListIcon fontSize="small" />
              <span>Filter</span>
            </button>

            <button
              type="button"
              onClick={handleRefresh}
              disabled={isRefreshing}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 16px",
                borderRadius: 999,
                border: "1px solid #E5E7EB",
                backgroundColor: isRefreshing ? "#F3F4F6" : "#FFFFFF",
                fontSize: 14,
                cursor: isRefreshing ? "not-allowed" : "pointer",
                color: "#374151",
              }}
            >
              {isRefreshing ? (
                <CircularProgress size={16} />
              ) : (
                <RefreshIcon fontSize="small" />
              )}
              <span>{isRefreshing ? "Refreshing..." : "Refresh"}</span>
            </button>
          </Stack>

          {/* Add button */}
          <button
            type="button"
            onClick={() => {
                if (tab === 0 && isSuperAdmin) {
                  setEditCompany(null);
                  setIsCompanyModalOpen(true);
                } else {
                  setEditUser(null);
                  setIsUserModalOpen(true);
                }
              }}              
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              backgroundColor: "#F7D154",
              color: "#111827",
              borderRadius: 999,
              border: "none",
              padding: "8px 18px",
              fontWeight: 600,
              fontSize: 14,
              cursor: "pointer",
            }}
          >
            <AddIcon fontSize="small" />
            {tab === 0 && isSuperAdmin ? "Add Company" : "Add User"}
          </button>
        </Box>

        {/* Table */}
        <Box sx={{ px: { xs: 2, sm: 3 }, pb: 3 }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: "16px",
              overflow: "hidden",
              border: "1px solid #E5E7EB",
            }}
          >
            <Box sx={{ overflowX: "auto" }}>
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: "#F6F6F6",
                    "& .MuiTableCell-root": {
                      fontWeight: 600,
                      fontSize: 13,
                      color: "#4B5563",
                      borderBottom: "1px solid #E5E7EB",
                    },
                  }}
                >
                  {tab === 0 ? (
                    <>
                      <TableCell sx={{ px: 3 }}>Company Name</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right" sx={{ pr: 3 }}>
                        Action
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell sx={{ px: 3 }}>Users</TableCell>
                      <TableCell>Company Name</TableCell>
                      {/* <TableCell>Type</TableCell> */}
                      <TableCell>Role</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right" sx={{ pr: 3 }}>
                        Action
                      </TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {tab === 0 && isSuperAdmin ? (
                  companiesLoading ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                        Loading companies...
                      </TableCell>
                    </TableRow>
                  ) : filteredCompanies.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                        <Typography
                          sx={{ fontSize: 14, color: "#9CA3AF" }}
                        >
                          No companies found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredCompanies.map((company) => (
                      <TableRow
                        key={company.id}
                        sx={{
                          "& .MuiTableCell-root": {
                            fontSize: 14,
                            borderBottom: "1px solid #E5E7EB",
                          },
                        }}
                      >
                        <TableCell sx={{ px: 3 }}>{company.name}</TableCell>
                        <TableCell>{renderCompanyType(company)}</TableCell>
                        <TableCell>
                          {renderCompanyStatus(company.status)}
                        </TableCell>
                        <TableCell align="right" sx={{ pr: 3 }}>
                          <IconButton
                            size="small"
                            onClick={(e) =>
                              handleCompanyMenuOpen(e, company)
                            }
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )
                ) : usersLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      Loading users...
                    </TableCell>
                  </TableRow>
                ) : filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography sx={{ fontSize: 14, color: "#9CA3AF" }}>
                        No users found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((user) => (
                    <TableRow
                      key={user.id}
                      sx={{
                        "& .MuiTableCell-root": {
                          fontSize: 14,
                          borderBottom: "1px solid #E5E7EB",
                        },
                      }}
                    >
                      <TableCell sx={{ px: 3 }}>
                        {user.firstName} {user.lastName}
                      </TableCell>
                      <TableCell>{user.companyName}</TableCell>
                      {/* <TableCell>
                        {user.isParent ? "Parent" : "Child"}
                      </TableCell> */}
                      <TableCell>{renderUserRole(user)}</TableCell>
                      <TableCell>{renderUserStatus(user.status)}</TableCell>
                      <TableCell align="right" sx={{ pr: 3 }}>
                        <IconButton
                          size="small"
                          onClick={(e) => handleUserMenuOpen(e, user)}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            </Box>
          </Paper>
        </Box>
      </Paper>

      {/* Company Menu */}
      {isSuperAdmin && (
        <Menu
            anchorEl={companyMenuAnchor}
            open={Boolean(companyMenuAnchor)}
            onClose={handleCompanyMenuClose}
        >
            <MenuItem
            onClick={() => {
                const row = companyMenuRow;
                handleCompanyMenuClose();
                setEditCompany(row);
                setIsCompanyModalOpen(true);
            }}
            >
            Edit Company
            </MenuItem>
            <MenuItem onClick={() => handleToggleCompanyStatus(companyMenuRow)}>
            {companyMenuRow?.status === 0
                ? "Mark as Inactive"
                : "Mark as Active"}
            </MenuItem>
            <MenuItem
            onClick={() => {
              const row = companyMenuRow;
              handleCompanyMenuClose();
              setConfirmDelete({ type: "company", row });
            }}
            sx={{ color: "#DC2626" }}
            >
            Delete Company
            </MenuItem>
        </Menu>
      )}

      {/* User Menu */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
      >
        <MenuItem
          onClick={() => {
            const row = userMenuRow;
            handleUserMenuClose();
            setEditUser(row);
            setIsUserModalOpen(true);
          }}
        >
          Edit User
        </MenuItem>
        <MenuItem onClick={() => handleToggleUserStatus(userMenuRow)}>
          {userMenuRow?.status === 1
            ? "Mark as Inactive"
            : "Mark as Active"}
        </MenuItem>
        <MenuItem
          onClick={() => {
            const row = userMenuRow;
            handleUserMenuClose();
            setConfirmDelete({ type: "user", row });
          }}
          sx={{ color: "#DC2626" }}
        >
          Delete User
        </MenuItem>
      </Menu>

      {/* Delete confirmation dialog */}
      <Dialog
        open={Boolean(confirmDelete)}
        onClose={() => (!isDeleting ? setConfirmDelete(null) : null)}
      >
        <DialogTitle>
          {confirmDelete?.type === "company" ? "Delete Company" : "Delete User"}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14 }}>
            {confirmDelete?.type === "company"
              ? `Are you sure you want to delete "${confirmDelete?.row?.name || "this company"}"? This cannot be undone.`
              : `Are you sure you want to delete "${[confirmDelete?.row?.firstName, confirmDelete?.row?.lastName].filter(Boolean).join(" ") || "this user"}"? This cannot be undone.`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ pb: 2, pr: 2 }}>
          <Button onClick={() => setConfirmDelete(null)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteAction}
            disabled={isDeleting}
            variant="contained"
            color="error"
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filter Popover */}
      <Popover
        open={filterOpen}
        anchorEl={filterAnchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            borderRadius: "24px",
            px: 4,
            py: 3,
            boxShadow: "0 20px 35px rgba(15,23,42,0.12)",
          },
        }}
      >
        <Box sx={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <Box>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>Type</Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={activeFilter.parent}
                  onChange={() =>
                    setActiveFilter((prev) => ({
                      ...prev,
                      parent: !prev.parent,
                    }))
                  }
                  size="small"
                />
              }
              label="Parent"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={activeFilter.child}
                  onChange={() =>
                    setActiveFilter((prev) => ({
                      ...prev,
                      child: !prev.child,
                    }))
                  }
                  size="small"
                />
              }
              label="Child"
            />
          </Box>

          <Box>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>Status</Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={activeFilter.active}
                  onChange={() =>
                    setActiveFilter((prev) => ({
                      ...prev,
                      active: !prev.active,
                    }))
                  }
                  size="small"
                />
              }
              label="Active"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={activeFilter.inactive}
                  onChange={() =>
                    setActiveFilter((prev) => ({
                      ...prev,
                      inactive: !prev.inactive,
                    }))
                  }
                  size="small"
                />
              }
              label="Inactive"
            />
          </Box>

          <Box>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>Sort By</Typography>
            {SORT_OPTIONS.map((option) => (
              <FormControlLabel
                key={option.value}
                control={
                  <Radio
                    size="small"
                    checked={(activeFilter.sort || "recency") === option.value}
                    onChange={() =>
                      setActiveFilter((prev) => ({
                        ...prev,
                        sort: option.value,
                      }))
                    }
                  />
                }
                label={option.label}
              />
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 3,
          }}
        >
          <button
            type="button"
            onClick={handleClearFilters}
            style={{
              border: "none",
              background: "transparent",
              color: "#B45309",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Clear
          </button>

          <button
            type="button"
            onClick={handleFilterClose}
            style={{
              border: "none",
              borderRadius: 999,
              backgroundColor: "#F7D154",
              color: "#111827",
              padding: "8px 28px",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Apply
          </button>
        </Box>
      </Popover>

      {/* Modals */}
      {isSuperAdmin && (
        <AddCompanyModal
            isOpen={isCompanyModalOpen}
            onClose={() => {
            setIsCompanyModalOpen(false);
            setEditCompany(null);
            }}
            editData={editCompany}
        />
      )}

      <AddUserModal
        isOpen={isUserModalOpen}
        onClose={() => {
          setIsUserModalOpen(false);
          setEditUser(null);
        }}
        editData={editUser}
      />
    </Box>
  );
};

export default UserCompanyTable;
